import { S } from '../mesh/surfaces';

// Decoration catalogue and room recipes. The reference loads these from binary UE DataTables; only a
// one-row CSV stub ships with it (SPEC §6.6, A12). This catalogue uses the reference's tag vocabulary
// (ExtractTags, P/ProceduralDecorationActor.cpp:132-390) and FDecoration's fields.

export interface Decoration {
  name: string;
  tags: string[];
  /** occupiedArea[0]: X (width along the wall), Y (depth), Z (height), cm. */
  size: [number, number, number];
  /** alignItem Z offset (cm): wall items hang above the floor. */
  lift: number;
  /** Main colour (parts of the model without their own colour). */
  color: [number, number, number];
  /** Key into MODELS (models.ts). */
  model: string;
}

/** FareaRecipeIngredient (P/Misc.h:73-99). chance/must/minNum/maxNum are unused by the reference too. */
export interface Ingredient {
  name: string;
  tagsMust: string[];
  tagsMustNot: string[];
  /** Most placements of this ingredient per room (the reference's unused maxNum; default: every attempt). */
  maxNum?: number;
  /** Placement attempts (default DECOR.repeatPerIngredient); more for a room's key item. */
  tries?: number;
}

/** FareaTypeRecipe (P/Misc.h:101-140): major items go against walls, minor items free-standing. */
export interface Recipe {
  name: string;
  majorItem: Ingredient[];
  minorItem: Ingredient[];
  /** Our own: floor finish and wall tint for rooms of this type. */
  floor: [number, number, number];
  walls: [number, number, number][];
}

const d = (name: string, tags: string[], size: [number, number, number], color: [number, number, number], model: string, lift = 0): Decoration => ({ name, tags, size, lift, color, model });

/**
 * The item list follows the reference's decoration DataTable (its rows' mesh paths: ArchVizInterior,
 * FreeFurniturePack, Restaurant, SMART-ArchVizPack, Bunker) and its tag vocabulary; sizes are real
 * furniture sizes. "Set" items (workstation, meeting set, dining set, toilet stall) carry their chairs
 * or partitions inside one footprint, so the placement pass keeps them together (DECISIONS).
 */
export const DECORATIONS: Decoration[] = [
  // office
  d('workstation', ['table', 'office', 'desk'], [160, 140, 110], [0.78, 0.74, 0.66], 'workstation'),
  d('workstation_dark', ['table', 'office', 'desk'], [150, 140, 110], [0.42, 0.32, 0.24], 'workstation'),
  d('executive_desk', ['table', 'office', 'executive'], [200, 170, 115], [0.36, 0.24, 0.16], 'executive'),
  d('desk_pair', ['table', 'office', 'cluster'], [160, 280, 110], [0.82, 0.8, 0.76], 'deskPair'),
  d('desk_pair_oak', ['table', 'office', 'cluster'], [180, 280, 110], [0.62, 0.48, 0.32], 'deskPair'),
  d('office_chair', ['chair', 'office'], [60, 60, 100], [0.15, 0.15, 0.17], 'officeChair'),
  d('filing_cabinet', ['cabinet', 'drawers', 'storage', 'office'], [50, 62, 132], [0.62, 0.63, 0.65], 'filing'),
  d('filing_cabinet_beige', ['cabinet', 'drawers', 'storage', 'office'], [50, 62, 132], [0.78, 0.74, 0.64], 'filing'),
  d('bookcase', ['bookcase', 'library', 'storage', 'office'], [100, 35, 200], [0.45, 0.33, 0.22], 'bookcase'),
  d('bookcase_white', ['bookcase', 'library', 'storage', 'office'], [90, 35, 190], [0.9, 0.9, 0.88], 'bookcase'),
  d('credenza', ['cabinet', 'storage', 'meeting'], [180, 50, 80], [0.4, 0.28, 0.18], 'credenza'),
  d('printer', ['printer', 'office'], [70, 60, 100], [0.72, 0.72, 0.72], 'printer'),
  d('water_dispenser', ['waterdispenser', 'office'], [35, 35, 120], [0.85, 0.85, 0.85], 'water'),
  d('trashcan', ['trashcan', 'bin'], [32, 32, 50], [0.3, 0.3, 0.32], 'trashcan'),
  d('trashcan_green', ['trashcan', 'bin'], [40, 40, 60], [0.2, 0.4, 0.25], 'trashcan'),
  d('plant', ['plant', 'fluff'], [45, 45, 110], [0.75, 0.72, 0.68], 'plant'),
  d('plant_large', ['plant', 'fluff'], [70, 70, 180], [0.3, 0.28, 0.26], 'tallPlant'),
  d('cactus', ['plant', 'cactus', 'fluff'], [35, 35, 90], [0.7, 0.45, 0.3], 'plant'),
  d('meeting_set', ['table', 'meeting'], [320, 230, 100], [0.4, 0.3, 0.22], 'meetingSet'),
  d('meeting_set_light', ['table', 'meeting'], [280, 220, 100], [0.85, 0.85, 0.82], 'meetingSet'),
  d('round_meeting', ['table', 'round', 'meeting'], [230, 230, 100], [0.62, 0.5, 0.36], 'roundMeeting'),
  // lounge
  d('sofa', ['sofa', 'couch', 'lounge'], [210, 90, 85], [0.3, 0.38, 0.55], 'sofa'),
  d('sofa_leather', ['sofa', 'couch', 'lounge', 'classic'], [220, 95, 85], [0.35, 0.2, 0.12], 'sofa'),
  d('sofa_modern', ['sofa', 'couch', 'lounge', 'modern'], [240, 90, 80], [0.6, 0.6, 0.58], 'sofa'),
  d('armchair', ['chair', 'armchair', 'lounge'], [85, 85, 95], [0.55, 0.3, 0.28], 'armchair'),
  d('armchair_leather', ['chair', 'armchair', 'lounge', 'classic'], [90, 90, 95], [0.3, 0.18, 0.1], 'armchair'),
  d('coffee_table', ['table', 'coffee', 'lounge'], [110, 60, 45], [0.4, 0.3, 0.2], 'coffeeTable'),
  d('floor_lamp', ['lamp', 'floor light', 'lounge'], [40, 40, 170], [0.9, 0.9, 0.85], 'floorLamp'),
  // kitchen / break room
  d('kitchen_counter', ['kitchen', 'sink', 'counter'], [240, 65, 92], [0.9, 0.9, 0.88], 'counter'),
  d('kitchen_counter_wood', ['kitchen', 'sink', 'counter'], [200, 65, 92], [0.55, 0.4, 0.26], 'counter'),
  d('fridge', ['kitchen', 'fridge', 'refrigerator'], [80, 75, 185], [0.88, 0.88, 0.9], 'fridge'),
  d('vending_machine', ['vendingmachine', 'drink', 'soda'], [90, 80, 190], [0.7, 0.15, 0.15], 'vending'),
  d('vending_snacks', ['vendingmachine', 'food'], [90, 80, 190], [0.15, 0.3, 0.6], 'vending'),
  d('dining_set', ['table', 'dining', 'kitchen'], [190, 190, 100], [0.62, 0.5, 0.36], 'diningSet'),
  d('dining_set_white', ['table', 'dining', 'kitchen'], [180, 180, 100], [0.9, 0.9, 0.88], 'diningSet'),
  d('bar_stool', ['stool', 'kitchen'], [40, 40, 75], [0.2, 0.2, 0.22], 'stool'),
  d('bench', ['bench', 'lounge'], [180, 50, 45], [0.5, 0.4, 0.3], 'bench'),
  // bathroom
  d('toilet_stall', ['toilet', 'bathroom', 'stall'], [100, 150, 200], [0.95, 0.95, 0.96], 'toiletStall'),
  d('toilet', ['toilet', 'bathroom'], [45, 70, 80], [0.95, 0.95, 0.96], 'toilet'),
  d('vanity', ['sink', 'bathroom'], [140, 55, 90], [0.5, 0.52, 0.55], 'vanity'),
  d('bathtub', ['bath', 'bathtub', 'bathroom'], [170, 80, 60], [0.95, 0.95, 0.96], 'bathtub'),
  // storage
  d('shelving', ['shelf', 'storage'], [180, 50, 200], [0.45, 0.47, 0.5], 'shelving'),
  d('locker', ['locker', 'storage'], [90, 50, 190], [0.35, 0.45, 0.55], 'locker'),
  d('boxes', ['box', 'cardboard', 'crate'], [90, 90, 100], [0.62, 0.47, 0.3], 'boxes'),
  d('crate', ['crate', 'box'], [80, 80, 70], [0.55, 0.42, 0.26], 'crate'),
  d('barrels', ['barrel', 'barrels'], [110, 110, 95], [0.3, 0.4, 0.55], 'barrels'),
  d('toolbox', ['toolbox', 'box'], [60, 30, 30], [0.75, 0.2, 0.15], 'crate'),
  // home (FreeFurniturePack beds and bedside tables, SMART-ArchVizPack dressers, TV tables, washer)
  d('bed_double', ['bed', 'bedroom'], [160, 205, 100], [0.45, 0.55, 0.7], 'bed'),
  d('bed_double_warm', ['bed', 'bedroom'], [160, 205, 100], [0.7, 0.5, 0.4], 'bed'),
  d('bed_single', ['bed', 'bedroom', 'single'], [95, 200, 95], [0.55, 0.65, 0.5], 'bed'),
  d('nightstand', ['nightstand', 'bedroom'], [50, 42, 60], [0.5, 0.36, 0.24], 'nightstand'),
  d('wardrobe', ['wardrobe', 'bedroom', 'storage'], [150, 62, 210], [0.82, 0.8, 0.76], 'wardrobe'),
  d('wardrobe_oak', ['wardrobe', 'bedroom', 'storage'], [120, 60, 205], [0.55, 0.4, 0.26], 'wardrobe'),
  d('dresser', ['dresser', 'drawers', 'bedroom'], [120, 50, 85], [0.6, 0.45, 0.3], 'dresser'),
  d('home_desk', ['home', 'writing'], [120, 130, 105], [0.7, 0.6, 0.45], 'workstation'),
  d('tv_stand', ['tv', 'stand', 'living'], [160, 45, 110], [0.3, 0.25, 0.2], 'tvStand'),
  d('shower', ['shower', 'bath', 'bathroom'], [95, 95, 210], [0.9, 0.9, 0.9], 'shower'),
  d('washing_machine', ['washingmachine', 'bathroom'], [60, 60, 85], [0.93, 0.93, 0.93], 'washer'),
  d('stove', ['oven', 'stove', 'kitchen'], [60, 62, 92], [0.85, 0.85, 0.85], 'stove'),
  d('kitchen_run', ['kitchen', 'counter', 'home'], [180, 62, 92], [0.62, 0.66, 0.6], 'counter'),
  d('shoe_cabinet', ['shoe', 'cabinet', 'entry'], [90, 35, 100], [0.85, 0.82, 0.76], 'shoeCabinet'),
  // ceiling
  d('ceiling_light', ['lamp', 'ceiling', 'light'], [120, 30, 6], [0.98, 0.97, 0.9], 'panelLight'),
  d('ceiling_panel', ['lamp', 'ceiling', 'light'], [60, 60, 5], [0.98, 0.98, 0.95], 'panelLight'),
  d('ceiling_round', ['lamp', 'ceiling', 'light', 'lounge'], [50, 50, 12], [0.98, 0.94, 0.8], 'roundLight'),
  d('ceiling_fan', ['fan', 'ceiling'], [110, 110, 40], [0.85, 0.85, 0.85], 'fan'),
  d('security_camera', ['cctv', 'securitycamera', 'ceiling'], [20, 30, 25], [0.2, 0.2, 0.22], 'camera'),
  // wall
  d('picture', ['picture', 'art', 'wall'], [80, 4, 60], [0.7, 0.5, 0.3], 'picture', 140),
  d('picture_wide', ['picture', 'art', 'wall'], [140, 4, 70], [0.35, 0.5, 0.65], 'picture', 135),
  d('poster', ['poster', 'picture', 'wall'], [60, 2, 85], [0.8, 0.35, 0.25], 'picture', 130),
  d('whiteboard', ['blackboard', 'wall', 'office', 'meeting'], [180, 3, 100], [0.95, 0.95, 0.95], 'whiteboard', 100),
  d('pinboard', ['pinboard', 'wall', 'office'], [120, 3, 90], [0.7, 0.55, 0.38], 'pinboard', 110),
  d('clock', ['clock', 'wall'], [35, 5, 35], [0.95, 0.95, 0.9], 'clock', 200),
  d('mirror', ['mirror', 'wall', 'bathroom'], [100, 3, 80], [0.75, 0.82, 0.88], 'mirror', 110),
  d('wall_tv', ['tv', 'screen', 'wall'], [140, 8, 80], [0.1, 0.1, 0.1], 'wallTv', 110),
  d('fire_extinguisher', ['extinguisher', 'wall', 'safety'], [20, 20, 55], [0.75, 0.1, 0.08], 'extinguisher', 40),
];

const ing = (name: string, tagsMust: string[], tagsMustNot: string[] = [], maxNum?: number): Ingredient => ({ name, tagsMust, tagsMustNot, maxNum });
const one = (name: string, tagsMust: string[]): Ingredient => ing(name, tagsMust, [], 1);
const few = (name: string, tagsMust: string[]): Ingredient => ing(name, tagsMust, [], 2);
/** A room's key item: one, with extra attempts to find it a place. */
const key = (name: string, tagsMust: string[]): Ingredient => ({ ...ing(name, tagsMust, [], 1), tries: 12 });

const CARPET: [number, number, number] = [0.42, 0.45, 0.5];
const WOOD: [number, number, number] = [0.62, 0.5, 0.36];
const TILE: [number, number, number] = [0.85, 0.87, 0.88];
const LINO: [number, number, number] = [0.66, 0.64, 0.56];
const CONCRETE: [number, number, number] = [0.52, 0.52, 0.5];

/** Texture surface of a recipe's floor colour (surfaces.ts). */
export function floorSurface(c: [number, number, number]): number {
  if (c === CARPET) return S.carpet;
  if (c === WOOD) return S.wood;
  if (c === TILE || c === LINO) return S.tileWhite;
  return S.concrete;
}
const OFFICE_WALLS: [number, number, number][] = [
  [0.86, 0.84, 0.8],
  [0.78, 0.82, 0.86],
  [0.84, 0.8, 0.72],
  [0.9, 0.9, 0.9],
];

/**
 * Room recipes. The reference ships three rows: "testRow" (every room, P/SpawnRoom.cpp:332),
 * "hallway" (P/SpawnBuildingFloor.cpp:2078) and "bathRow" (in areaTypeRecipe.uasset, unused by the
 * code). The others are ours, built from the same tag vocabulary; rooms get one by size (roomRecipe).
 */
export const RECIPES = {
  testRow: {
    name: 'office',
    majorItem: [ing('desk', ['table', 'office'], ['cluster']), ing('storage', ['storage', 'office']), ing('second desk', ['desk'])],
    minorItem: [few('guest chair', ['armchair']), few('greenery', ['plant']), few('bins', ['trashcan'])],
    floor: CARPET,
    walls: OFFICE_WALLS,
  },
  openOffice: {
    name: 'open office',
    majorItem: [ing('desks', ['desk']), ing('storage', ['cabinet', 'office']), ing('more desks', ['desk']), one('printer', ['printer']), ing('shelves', ['bookcase'])],
    minorItem: [ing('clusters', ['cluster']), ing('clusters', ['cluster']), few('greenery', ['plant']), one('water', ['waterdispenser']), few('bins', ['trashcan'])],
    floor: CARPET,
    walls: OFFICE_WALLS,
  },
  meeting: {
    name: 'meeting room',
    majorItem: [one('sideboard', ['cabinet', 'meeting']), few('greenery', ['plant'])],
    minorItem: [ing('table', ['table', 'meeting'])],
    floor: CARPET,
    walls: [
      [0.8, 0.8, 0.78],
      [0.7, 0.75, 0.8],
    ],
  },
  breakRoom: {
    name: 'break room',
    majorItem: [ing('counter', ['counter']), one('fridge', ['fridge']), one('vending', ['vendingmachine']), one('water', ['waterdispenser'])],
    minorItem: [ing('tables', ['dining']), ing('tables', ['dining']), few('bins', ['trashcan']), few('greenery', ['plant'])],
    floor: LINO,
    walls: [
      [0.92, 0.88, 0.76],
      [0.82, 0.88, 0.8],
    ],
  },
  lounge: {
    name: 'lounge',
    majorItem: [ing('sofa', ['sofa']), ing('armchair', ['armchair']), ing('shelves', ['bookcase']), few('lamp', ['floor light'])],
    minorItem: [few('coffee table', ['coffee']), few('greenery', ['plant']), ing('sofa', ['sofa'])],
    floor: WOOD,
    walls: [
      [0.82, 0.74, 0.66],
      [0.7, 0.72, 0.66],
    ],
  },
  bathRow: {
    name: 'bathroom',
    majorItem: [ing('stalls', ['toilet']), ing('sinks', ['sink', 'bathroom']), ing('stalls', ['toilet']), ing('stalls', ['toilet']), ing('sinks', ['sink', 'bathroom'])],
    minorItem: [few('bins', ['trashcan'])],
    floor: TILE,
    walls: [
      [0.88, 0.9, 0.92],
      [0.8, 0.86, 0.9],
    ],
  },
  storage: {
    name: 'storage',
    majorItem: [ing('shelving', ['shelf']), ing('lockers', ['locker']), ing('cabinets', ['cabinet', 'drawers'])],
    minorItem: [ing('boxes', ['box']), ing('barrels', ['barrel']), ing('crates', ['crate'])],
    floor: CONCRETE,
    walls: [[0.7, 0.7, 0.68]],
  },
  // Residential rooms (our own, see residential.ts).
  living: {
    name: 'living room',
    majorItem: [key('sofa', ['sofa']), one('tv', ['tv', 'stand']), ing('armchair', ['armchair']), one('shelves', ['bookcase']), few('lamp', ['floor light'])],
    minorItem: [one('coffee table', ['coffee']), one('dining', ['dining']), few('greenery', ['plant'])],
    floor: WOOD,
    walls: [
      [0.9, 0.86, 0.8],
      [0.82, 0.84, 0.78],
      [0.86, 0.8, 0.74],
    ],
  },
  kitchen: {
    name: 'kitchen',
    majorItem: [key('counter', ['kitchen', 'counter']), one('fridge', ['fridge']), one('stove', ['stove']), one('shoes', ['shoe'])],
    minorItem: [one('table', ['dining']), one('bins', ['trashcan'])],
    floor: LINO,
    walls: [
      [0.92, 0.9, 0.84],
      [0.86, 0.9, 0.88],
    ],
  },
  bedroom: {
    name: 'bedroom',
    majorItem: [key('bed', ['bed']), few('bedside', ['nightstand']), one('wardrobe', ['wardrobe']), one('dresser', ['dresser']), one('desk', ['writing'])],
    minorItem: [one('greenery', ['plant'])],
    floor: WOOD,
    walls: [
      [0.8, 0.84, 0.88],
      [0.88, 0.84, 0.86],
      [0.86, 0.86, 0.8],
    ],
  },
  homeBath: {
    name: 'bathroom',
    majorItem: [ing('toilet', ['toilet'], ['stall'], 1), one('sink', ['sink', 'bathroom']), one('bath', ['bath']), one('washer', ['washingmachine'])],
    minorItem: [one('bin', ['trashcan'])],
    floor: TILE,
    walls: [
      [0.9, 0.92, 0.94],
      [0.84, 0.9, 0.9],
    ],
  },
  study: {
    name: 'study',
    majorItem: [key('desk', ['writing']), one('shelves', ['bookcase']), one('chair', ['armchair'])],
    minorItem: [one('greenery', ['plant'])],
    floor: WOOD,
    walls: [[0.84, 0.82, 0.76]],
  },
  studio: {
    name: 'studio',
    majorItem: [key('bed', ['bed']), key('kitchen', ['kitchen', 'counter']), one('fridge', ['fridge']), ing('toilet', ['toilet'], ['stall'], 1), one('sink', ['sink', 'bathroom']), one('sofa', ['sofa']), one('wardrobe', ['wardrobe'])],
    minorItem: [one('table', ['dining']), one('greenery', ['plant'])],
    floor: WOOD,
    walls: [[0.9, 0.87, 0.82]],
  },
  // Row houses (rowhouse.ts).
  landing: {
    name: 'landing',
    majorItem: [one('shoes', ['shoe']), one('greenery', ['plant'])],
    minorItem: [],
    floor: WOOD,
    walls: [
      [0.9, 0.88, 0.82],
      [0.86, 0.86, 0.84],
    ],
  },
  stairs: {
    name: 'stairwell',
    majorItem: [],
    minorItem: [],
    floor: WOOD,
    walls: [[0.9, 0.88, 0.82]],
  },
  hallway: {
    name: 'hallway',
    majorItem: [few('benches', ['bench']), one('vending', ['vendingmachine']), few('greenery', ['plant'])],
    minorItem: [few('bins', ['trashcan']), one('water', ['waterdispenser'])],
    floor: [0.5, 0.48, 0.45],
    walls: [[0.8, 0.78, 0.74]],
  },
} satisfies Record<string, Recipe>;

export type RecipeName = keyof typeof RECIPES;

/** Ceiling and wall ingredients per recipe: the decorator's ceiling and wall passes use these. */
export const CEILING_WALL: Record<RecipeName, { ceiling: Ingredient[]; wall: Ingredient[] }> = {
  testRow: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [ing('art', ['picture']), few('boards', ['pinboard']), one('clock', ['clock'])] },
  openOffice: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [few('boards', ['blackboard']), one('clock', ['clock']), few('safety', ['extinguisher']), ing('art', ['picture'])] },
  meeting: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [one('screen', ['tv']), few('boards', ['blackboard']), ing('art', ['picture'])] },
  breakRoom: { ceiling: [ing('lights', ['light'], ['lounge']), ing('fans', ['fan'])], wall: [one('clock', ['clock']), one('tv', ['tv']), ing('posters', ['poster'])] },
  lounge: { ceiling: [ing('lights', ['light', 'lounge'])], wall: [ing('art', ['picture']), ing('art', ['picture']), one('tv', ['tv'])] },
  bathRow: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [ing('mirrors', ['mirror']), ing('mirrors', ['mirror'])] },
  storage: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [few('safety', ['extinguisher'])] },
  living: { ceiling: [ing('lights', ['light', 'lounge'])], wall: [ing('art', ['picture']), ing('art', ['picture']), one('clock', ['clock'])] },
  kitchen: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [one('clock', ['clock'])] },
  bedroom: { ceiling: [ing('lights', ['light', 'lounge'])], wall: [ing('art', ['picture'])] },
  homeBath: { ceiling: [ing('lights', ['light'], ['lounge'])], wall: [one('mirror', ['mirror'])] },
  studio: { ceiling: [ing('lights', ['light', 'lounge'])], wall: [ing('art', ['picture'])] },
  study: { ceiling: [ing('lights', ['light', 'lounge'])], wall: [ing('art', ['picture']), one('board', ['pinboard'])] },
  landing: { ceiling: [ing('lights', ['light', 'lounge'])], wall: [one('art', ['picture']), one('mirror', ['mirror'])] },
  stairs: { ceiling: [], wall: [] },
  hallway: { ceiling: [ing('lights', ['light'], ['lounge']), few('cameras', ['cctv'])], wall: [ing('art', ['picture']), few('clocks', ['clock']), few('safety', ['extinguisher'])] },
};

/**
 * Recipes for a floor's office-type rooms (our own; the reference decorates every room with "testRow").
 * The floor plans produce large rooms (median ≈ 250 m²), so types go by rank within the floor: the
 * smallest room is the bathroom, the next a break room or storage, the next a meeting room or lounge,
 * the rest mostly open-plan offices. Rooms under 35 m² use their size instead. `h` is a per-room hash.
 */
export function roomRecipes(rooms: { area: number; h: number }[]): RecipeName[] {
  const out: RecipeName[] = rooms.map(({ area, h }) => {
    if (area < 8) return h < 0.55 ? 'storage' : 'bathRow';
    if (area < 16) return h < 0.55 ? 'testRow' : h < 0.8 ? 'bathRow' : 'storage';
    if (area < 35) return h < 0.45 ? 'testRow' : h < 0.7 ? 'meeting' : h < 0.85 ? 'breakRoom' : 'lounge';
    return h < 0.7 ? 'openOffice' : h < 0.8 ? 'testRow' : h < 0.9 ? 'meeting' : 'lounge';
  });
  const big = rooms.map((r, i) => ({ ...r, i })).filter((r) => r.area >= 35);
  big.sort((a, b) => a.area - b.area || a.i - b.i);
  if (big.length >= 2) out[big[0].i] = 'bathRow';
  if (big.length >= 3) out[big[1].i] = big[1].h < 0.5 ? 'breakRoom' : 'storage';
  if (big.length >= 4) out[big[2].i] = big[2].h < 0.5 ? 'meeting' : 'lounge';
  return out;
}

/**
 * suggestObject (P/ProceduralDecorationActor.cpp:977-1056): filter by the ingredient's must/mustNot
 * tags plus "ceiling"/"wall" depending on the pass, then pick by noise.
 */
export function suggestObject(ingredient: Ingredient, pick: number, ceiling: boolean, wall: boolean): Decoration | null {
  const must = [...ingredient.tagsMust, ...(ceiling ? ['ceiling'] : []), ...(wall ? ['wall'] : [])];
  const not = [...ingredient.tagsMustNot, ...(ceiling ? [] : ['ceiling']), ...(wall ? [] : ['wall'])];
  const ok = DECORATIONS.filter((d) => must.every((t) => d.tags.includes(t)) && !not.some((t) => d.tags.includes(t)));
  if (ok.length === 0) return null;
  return ok[Math.min(ok.length - 1, Math.floor(ok.length * pick))];
}
